import { createSlice } from '@reduxjs/toolkit'
import { AES } from 'crypto-js'
import { AppThunk } from 'store/store'
import { authSliceType, ErrorMessage } from './Types/authSliceType'
import { login } from 'services/login'
import {
  AccessDataInterface,
  DataAuthRequest,
  UserPreferences
} from 'services/login/interfaces'
import { authErrors, unknownError } from 'commons/messages'
import { setLoginValidation } from 'store/StructuralContext/structuralSlice'
import { toast } from 'react-toastify'
import { sendCodeRequest } from 'services/forgetPassword'
import { CodeResponseStorage } from 'container/ForgetPassword/components/types/responses'

const initialState: authSliceType = {
  isLoading: false,
  cd_usua: JSON.parse(localStorage.getItem('cd_usua') || '{}').cd_usua || '',
  roles: JSON.parse(localStorage.getItem('roles') || '{}').roles || '',
  token: JSON.parse(localStorage.getItem('token') || '{}'),
  permission: JSON.parse(localStorage.getItem('permission') || '[]'),
  offices: JSON.parse(localStorage.getItem('offices') || '[]'),
  preferences: JSON.parse(localStorage.getItem('preferences') || '{}'),
  loginTries: 0,
  email: '',
  error: {
    title: '',
    description: ''
  },
  authData: {
    nr_cnpj_cpf: 0,
    ds_emai: '',
    ds_senh: ''
  }
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, { payload }) => ({
      ...state,
      isLoading: payload
    }),
    loginRequest: (state) => ({
      ...state,
      isLoading: true,
      error: {
        title: '',
        description: ''
      }
    }),
    loginConfirm: (state, { payload }) => ({
      ...state,
      isLoading: false,
      authData: payload.authData,
      email: payload.email
    }),
    loginSuccess: (state, { payload }) => ({
      ...state,
      isLoading: false,
      cd_usua: payload.cd_usua,
      roles: payload.roles,
      token: payload.token,
      permission: payload.permission,
      preferences: payload.preferences,
      offices: payload.offices
    }),
    loginFail: (state, { payload }) => ({
      ...state,
      isLoading: false,
      error: payload.error,
      loginTries: payload.tries,
      email: payload.email
    }),
    setEmail: (state, { payload }) => ({
      ...state,
      email: payload
    }),
    clearState: () => ({
      isLoading: false,
      cd_usua: '',
      roles: '',
      token: {},
      permission: [],
      offices: [],
      preferences: {} as UserPreferences,
      email: '',
      loginTries: 0,
      error: {
        title: '',
        description: ''
      },
      authData: {
        nr_cnpj_cpf: 0,
        ds_emai: '',
        ds_senh: ''
      }
    })
  }
})

const { actions, reducer } = authSlice
export const {
  setLoading,
  loginRequest,
  loginConfirm,
  setEmail,
  loginSuccess,
  loginFail,
  clearState
} = actions
export default reducer

export const handleLogout = (): AppThunk => (dispatch) => {
  localStorage.clear()
  dispatch(clearState())
}

export const handleAuth =
  (data: DataAuthRequest, successCallback: () => void): AppThunk =>
  async (dispatch) => {
    dispatch(loginRequest())

    const accessData = await login(data)

    if (accessData && accessData.Token) {
      const cd_usua = accessData.Codigo ?? data.cd_usua ?? ''
      const rolesEncrypted = AES.encrypt(
        accessData.RetornoWs.UserRole,
        'user-roles-key'
      ).toString()
      const ds_emai = accessData.Email ?? data.ds_emai ?? ''

      setAuthStorage(cd_usua, rolesEncrypted, accessData, ds_emai)
      dispatch(
        loginSuccess({
          cd_usua,
          roles: rolesEncrypted,
          token: accessData.Token,
          permission: accessData.RetornoWs.UserPermissions,
          preferences: accessData.RetornoWs.Preferences,
          offices: accessData.RetornoWs.EscritorioDiretor.map(
            (office) => office.cd_escr_vend
          )
        })
      )
      successCallback()
    } else if (accessData.RetornoWs.RetCod === '0') {
      dispatch(loginConfirm({ authData: data, email: accessData.Email }))
      dispatch(setLoginValidation(true))
      toast.success('Código enviado com sucesso!')
    } else if (accessData.RetornoWs.RetCod === '99') {
      dispatch(loginConfirm({ authData: data, email: accessData.Email }))
      dispatch(resendAuthCode({ ...data, fl_renv_cod: 'S' }))
      dispatch(setLoginValidation(true))
    } else {
      const { Email: email, TentativasAcesso: tries } = accessData
      const error = handleErrorMessage(accessData)
      dispatch(loginFail({ error, email, tries }))
    }
  }

export const resendAuthCode =
  (data: DataAuthRequest): AppThunk =>
  async () => {
    const accessData = await login(data)

    if (accessData.RetornoWs.RetCod === '0') {
      toast.success('Código enviado com sucesso!')
    } else {
      toast.error('Erro ao enviar código. Tente novamente.')
    }
  }

export const handleConfirmAuth =
  (
    data: DataAuthRequest,
    successCallback: () => void,
    errorCallback: () => void
  ): AppThunk =>
  async (dispatch) => {
    dispatch(loginRequest())

    const accessData = await login(data)

    if (accessData && accessData.Token) {
      const cd_usua = accessData.Codigo ?? data.cd_usua ?? ''
      const rolesEncrypted = AES.encrypt(
        accessData.RetornoWs.UserRole,
        'user-roles-key'
      ).toString()
      const ds_emai = accessData.Email ?? data.ds_emai ?? ''

      setAuthStorage(cd_usua, rolesEncrypted, accessData, ds_emai)
      dispatch(
        loginSuccess({
          cd_usua,
          roles: rolesEncrypted,
          token: accessData.Token,
          permission: accessData.RetornoWs.UserPermissions,
          preferences: accessData.RetornoWs.Preferences,
          offices: accessData.RetornoWs.EscritorioDiretor.map(
            (office) => office.cd_escr_vend
          )
        })
      )
      successCallback()
      dispatch(setLoginValidation(false))
    } else {
      const { Email: email, TentativasAcesso: tries } = accessData
      const error = handleErrorMessage(accessData)
      dispatch(loginFail({ error, email, tries }))
      errorCallback()
    }
  }

export const resetPasswordRequest =
  (email: string, successCallback: () => void): AppThunk =>
  async (dispatch) => {
    dispatch(setLoading(true))
    try {
      const response = await sendCodeRequest({
        ds_emai: email
      })
      const composeStorage: CodeResponseStorage = {
        id_usua: response.id_usua,
        nm_usua: response?.nm_usua || '',
        return_code: response.return_code,
        return_chav: response.return_chav,
        email: email
      }
      localStorage.setItem('resetPassword', JSON.stringify(composeStorage))
      successCallback()
    } catch (error) {
      toast.error('Erro ao solicitar redefinição de senha.')
    } finally {
      dispatch(setLoading(false))
    }
  }

const setAuthStorage = (
  cd_usua: string,
  roles: string,
  accessData: AccessDataInterface,
  ds_emai: string
) => {
  localStorage.setItem('token', JSON.stringify({ ...accessData.Token }))
  localStorage.setItem(
    'permission',
    JSON.stringify({ ...accessData.RetornoWs.UserPermissions })
  )
  localStorage.setItem('cd_usua', JSON.stringify({ cd_usua }))
  localStorage.setItem('ds_emai', JSON.stringify({ ds_emai }))
  localStorage.setItem('roles', JSON.stringify({ roles }))
  localStorage.setItem('preferences', JSON.stringify(accessData.RetornoWs.Preferences))
  localStorage.setItem(
    'offices',
    JSON.stringify(
      accessData.RetornoWs.EscritorioDiretor.map((office) => office.cd_escr_vend)
    )
  )
}

const handleErrorMessage = (accessData: AccessDataInterface): ErrorMessage => {
  const { RetCod } = accessData.RetornoWs

  return (RetCod && authErrors[RetCod]) || unknownError()
}
